import { Button, RefreshIcon } from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import { Separator, Text, View, XStack } from "tamagui";
import { useDebouncedCallback } from "use-debounce";

import { api } from "~/utils/api";
import { SearchInput } from "~/components/blocks";
import {
  UsersStatusSelect,
  useUsersTableFilter,
} from "~/components/blocks/filters/users-table-filter";
import { InviteToGroupDialog } from "./invite-to-group-dialog";

export const GroupHeader: React.FC<{ groupId: string }> = ({ groupId }) => {
  const { dispatch } = useUsersTableFilter();

  const { t } = useTranslation("group");

  const [data] = api.group.getGroupNameAndStatusById.useSuspenseQuery({
    groupId: groupId ?? "",
  });

  const isOrganization = groupId === "organization";

  const handleSearchInput = useDebouncedCallback(
    (input: string) =>
      dispatch({ type: "SET_FILTER", filterName: "search", value: input }),
    300,
  );

  return (
    <XStack justifyContent="space-between" alignItems="flex-start">
      <View alignItems="flex-start" flex={1} gap={"$3"}>
        <Text
          data-id="group-header-title"
          fontFamily={"$subtitle"}
          fontWeight={"$6"}
          fontSize={"$7"}
          lineHeight={"$7"}
        >
          {isOrganization
            ? t("group-header.title.organization")
            : t("group-header.title.group", { name: data[0]?.name })}
        </Text>
        {!isOrganization ? (
          <InviteToGroup groupId={groupId} disabled={!data[0]?.active} />
        ) : null}
      </View>

      <View
        maxWidth={400}
        width={"100%"}
        flex={1}
        alignItems="flex-end"
        gap={"$4.2"}
      >
        <SearchInput maxWidth={400} onChangeText={handleSearchInput} />
        <UsersStatusSelect />
      </View>
    </XStack>
  );
};

const InviteToGroup: React.FC<{
  groupId: string;
  disabled: boolean;
}> = ({ groupId, disabled }) => {
  const { t } = useTranslation("group");
  const utils = api.useUtils();

  const { mutate, isLoading } = api.person.resendInvitationBulk.useMutation({
    onSuccess: () => utils.person.list.invalidate(),
  });

  return (
    <XStack data-id="invite-to-group-container" gap={"$2"} alignItems="center">
      <InviteToGroupDialog groupId={groupId} />
      <Separator
        borderColor={"$gray-secondary-400"}
        vertical
        borderRightWidth={"$0.2"}
        height={"$3"}
      />
      <Button
        data-id="resend-invite-to-group-button"
        variant="outlined"
        size="md"
        rounded="lg"
        icon={<RefreshIcon />}
        disabled={isLoading || disabled}
        onPress={() => {
          mutate({ groupId });
        }}
      >
        {t("invite-user.invite-all-reminder")}
      </Button>
    </XStack>
  );
};
